<template>
                <div v-if="currentView === 'server_ai'">
                    <div class="page-header"><div><div class="page-title">AI 服務管理</div><div class="page-subtitle">管理 AI 模型設定、RAG 知識庫與智能助手</div></div></div>

                    <!-- Tabs -->
                    <div class="flex mb-6" style="border-bottom:1px solid var(--border);">
                        <button @click="aiTab = 'rag'" :class="aiTab === 'rag' ? 'active' : ''" class="tab-item">知識庫搜尋 (RAG)</button>
                        <button @click="aiTab = 'cbi'" :class="aiTab === 'cbi' ? 'active' : ''" class="tab-item">AI客戶背景情報 (CBI)</button>
                        <button @click="aiTab = 'settings'" :class="aiTab === 'settings' ? 'active' : ''" class="tab-item">AI 參數設定</button>
                    </div>

                        <!-- Marketing Tab Removed -->

                    <!-- RAG Tab -->
                    <div v-if="aiTab === 'rag'" class="card p-6">
                        <div class="flex justify-between items-center mb-6">
                            <h3 class="font-bold text-lg" style="color:var(--gray-700);">郵件知識庫搜尋 (Retrieval-Augmented Generation)</h3>
                            <div class="flex items-center gap-4">
                                <span class="text-sm" style="color:var(--gray-500);">已索引郵件數: <span class="font-bold" style="color:var(--gray-800);">{{
                                        ragStats.email_count ?? ragStats.count ?? 0 }}</span></span>
                                <span class="text-sm" style="color:var(--gray-500);">知識文件數: <span class="font-bold" style="color:var(--gray-800);">{{
                                        ragStats.knowledge_count ?? 0 }}</span></span>
                                <button @click="syncRagMaildir" :disabled="isSyncing"
                                    class="btn btn-dark btn-xs">
                                    {{ isSyncing ? syncMessage : '🔄 同步郵件庫' }}
                                </button>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div class="card p-4">
                                <div class="flex items-center justify-between gap-2 mb-3">
                                    <div class="font-bold" style="color:var(--gray-800);">查郵件知識庫</div>
                                    <button @click="performRagQuery" :disabled="ragLoading || !ragQueryInput"
                                        class="btn btn-primary btn-sm">
                                        <span v-if="ragLoading"
                                            class="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></span>
                                        {{ ragLoading ? '搜尋中...' : '🔍 搜尋' }}
                                    </button>
                                </div>
                                <input v-model="ragQueryInput" @keyup.enter="performRagQuery"
                                    class="input w-full" placeholder="例如：上週關於專案的郵件有哪些重點？">
                                <div v-if="ragResult" class="mt-3 rounded p-3" style="border:1px solid var(--border);background:var(--surface);">
                                    <div v-if="ragResult.error" class="text-sm" style="color:var(--color-danger);">
                                        {{ ragResult.error }}
                                    </div>
                                    <div v-else class="whitespace-pre-wrap text-sm" style="color:var(--gray-800);">{{ ragResult.result }}</div>
                                </div>
                            </div>

                            <div class="card p-4">
                                <div class="flex items-center justify-between gap-2 mb-3">
                                    <div class="font-bold" style="color:var(--gray-800);">查產品/方案知識庫（含引用）</div>
                                    <button @click="performKnowledgeQuery"
                                        :disabled="kbAdminLoading || !(kbAdminQueryInput && kbAdminQueryInput.trim())"
                                        class="btn btn-success btn-sm">
                                        <span v-if="kbAdminLoading"
                                            class="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></span>
                                        {{ kbAdminLoading ? '查詢中...' : '🔎 查詢' }}
                                    </button>
                                </div>
                                <input v-model="kbAdminQueryInput" @keyup.enter="performKnowledgeQuery"
                                    class="input w-full" placeholder="例如：價格規則？合約條款？案例摘要？">
                                <div v-if="kbAdminError" class="text-sm mt-2" style="color:var(--color-danger);">{{ kbAdminError }}</div>
                                <div v-else-if="kbAdminResult" class="mt-3 rounded p-3" style="border:1px solid var(--border);background:var(--surface);">
                                    <div class="whitespace-pre-wrap text-sm" style="color:var(--gray-800);">{{ kbAdminResult }}</div>
                                    <div v-if="kbAdminSources && kbAdminSources.length" class="mt-3">
                                        <div class="text-xs font-bold mb-1" style="color:var(--gray-700);">引用來源</div>
                                        <div class="space-y-1">
                                            <div v-for="(s, idx) in kbAdminSources" :key="'kb-admin-src-' + idx"
                                                class="text-[11px] truncate" style="color:var(--gray-600);">
                                                [{{ idx + 1 }}] {{ s.source_name || ('doc-' + (s.doc_id || '')) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card p-4 mt-6">
                            <div class="flex items-center justify-between gap-2 mb-3">
                                <div class="font-bold" style="color:var(--gray-800);">知識庫管理（新增/查看）</div>
                                <button @click="fetchKnowledgeDocuments" :disabled="kbDocsLoading"
                                    class="btn btn-secondary btn-sm">
                                    {{ kbDocsLoading ? '載入中...' : '重新整理' }}
                                </button>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div class="card p-3">
                                    <div class="text-sm font-bold mb-2" style="color:var(--gray-700);">新增文字文件</div>
                                    <input v-model="kbNewDocTitle" class="input .5"
                                        placeholder="標題（例如：價格規則 / 合約條款 / FAQ）」">
                                    <textarea v-model="kbNewDocContent" rows="6"
                                        class="input .5 mt-2"
                                        placeholder="貼上內容（純文字）"></textarea>
                                    <div class="mt-2 flex justify-end">
                                        <button @click="addKnowledgeDocument"
                                            :disabled="kbSaving || !(kbNewDocTitle && kbNewDocTitle.trim()) || !(kbNewDocContent && kbNewDocContent.trim())"
                                            class="btn btn-dark btn-sm">
                                            {{ kbSaving ? '新增中...' : '新增並索引' }}
                                        </button>
                                    </div>
                                </div>
                                <div class="card p-3">
                                    <div class="text-sm font-bold mb-2" style="color:var(--gray-700);">文件列表</div>
                                    <div v-if="kbDocsLoading" class="text-sm" style="color:var(--gray-500);">載入中...</div>
                                    <div v-else-if="kbDocs && kbDocs.length" class="space-y-2 max-h-56 overflow-y-auto">
                                        <div v-for="(d, idx) in safeArr(kbDocs)" :key="keyById(d, 'kb-doc', idx)"
                                            class="rounded-lg p-2" style="border:1px solid var(--border);background:var(--surface);">
                                            <div class="text-sm font-bold truncate" style="color:var(--gray-800);">{{ d.title }}</div>
                                            <div class="text-xs" style="color:var(--gray-500);">ID: {{ d.id }}｜已索引: {{ d.is_indexed ? '是' : '否' }}</div>
                                        </div>
                                    </div>
                                    <div v-else class="text-sm" style="color:var(--gray-500);">尚無文件</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="aiTab === 'cbi'">

                        <!-- Section Header -->
                        <div class="cbi-section-header mb-4">
                            <div class="flex items-center gap-2">
                                <span style="font-size:1.2rem;">🏢</span>
                                <div>
                                    <div class="font-bold text-base" style="color:#fff;">AI 客戶背景情報列表 (CBI)</div>
                                    <div class="text-xs mt-0.5" style="color:rgba(255,255,255,0.75);">所有 AI 分析過的客戶情報記錄</div>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <button @click="resetCbiFilters()" class="btn btn-sm"
                                    style="background:rgba(255,255,255,0.15);color:#fff;border:1px solid rgba(255,255,255,0.3);">
                                    清除條件
                                </button>
                                <button @click="fetchCbiInsights(true)" class="btn btn-sm"
                                    style="background:#fff;color:#4f46e5;font-weight:700;">
                                    <span v-if="cbiLoading" style="display:inline-block;animation:cbi-spin 1s linear infinite;" class="mr-1">⟳</span>
                                    重新載入
                                </button>
                            </div>
                        </div>

                        <!-- Filter Bar -->
                        <div class="card mb-4 p-4" style="background:var(--gray-50);">
                            <div class="grid grid-cols-1 md:grid-cols-6 gap-3">
                                <div class="md:col-span-2">
                                    <label class="cbi-filter-label">🔍 關鍵字</label>
                                    <input v-model="cbiFilters.search" @keyup.enter="fetchCbiInsights(true)"
                                        class="input" placeholder="主旨 / 寄件者 / 公司 / 網站" />
                                </div>
                                <div>
                                    <label class="cbi-filter-label">寄件者</label>
                                    <input v-model="cbiFilters.sender" @keyup.enter="fetchCbiInsights(true)"
                                        class="input" placeholder="email / name" />
                                </div>
                                <div>
                                    <label class="cbi-filter-label">公司</label>
                                    <input v-model="cbiFilters.company" @keyup.enter="fetchCbiInsights(true)"
                                        class="input" placeholder="公司名稱" />
                                </div>
                                <div>
                                    <label class="cbi-filter-label">狀態</label>
                                    <select v-model="cbiFilters.status" @change="fetchCbiInsights(true)" class="input">
                                        <option value="">全部</option>
                                        <option value="new">new</option>
                                        <option value="done">done</option>
                                        <option value="archived">archived</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="cbi-filter-label">每頁</label>
                                    <select v-model.number="cbiLimit" @change="fetchCbiInsights(true)" class="input">
                                        <option :value="20">20</option>
                                        <option :value="50">50</option>
                                        <option :value="100">100</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <!-- Loading -->
                        <div v-if="cbiLoading" class="card p-12 text-center" style="color:var(--gray-500);">
                            <div style="font-size:2.25rem;display:inline-block;animation:cbi-spin 1s linear infinite;" class="mb-3">⟳</div>
                            <div class="text-sm">載入中...</div>
                        </div>

                        <!-- Empty -->
                        <div v-else-if="!Array.isArray(cbiItems) || cbiItems.length === 0"
                            class="card p-12 text-center">
                            <div style="font-size:2.5rem;" class="mb-2">🏢</div>
                            <div class="font-medium mb-1" style="color:var(--gray-600);">尚無情報記錄</div>
                            <div class="text-sm" style="color:var(--gray-400);">在 Webmail 使用「AI 智能助手 → AI 客戶背景」即可產生情報</div>
                        </div>

                        <!-- Table -->
                        <div v-else class="card overflow-x-auto">
                            <table class="cbi-page-table">
                                <thead>
                                    <tr style="background:var(--gray-50);border-bottom:2px solid var(--gray-200);">
                                        <th class="cbi-th">時間</th>
                                        <th class="cbi-th">寄件者</th>
                                        <th class="cbi-th">主旨</th>
                                        <th class="cbi-th">公司 / 信心</th>
                                        <th class="cbi-th">共用狀態</th>
                                        <th v-if="isDomainAdmin || isSuperAdmin" class="cbi-th">授權</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="it in cbiItems" :key="it.id" class="cbi-page-row">
                                        <td class="cbi-td whitespace-nowrap" style="font-size:0.75rem;color:var(--gray-500);">
                                            {{ it.created_at ? new Date(it.created_at).toLocaleString() : '—' }}
                                        </td>
                                        <td class="cbi-td">
                                            <div class="font-medium truncate" style="max-width:180px;color:var(--gray-800);">{{ it.sender_name || '—' }}</div>
                                            <div class="text-xs truncate mt-0.5" style="max-width:180px;color:var(--gray-500);">{{ it.sender_email || '' }}</div>
                                        </td>
                                        <td class="cbi-td">
                                            <div class="truncate" style="max-width:280px;color:var(--gray-700);">{{ it.subject || '—' }}</div>
                                            <div v-if="it.website" class="text-xs truncate mt-0.5" style="max-width:280px;color:#4338ca;">{{ it.website }}</div>
                                        </td>
                                        <td class="cbi-td">
                                            <div class="font-medium truncate mb-1" style="max-width:180px;color:var(--gray-800);">{{ it.company_name || '—' }}</div>
                                            <span v-if="it.confidence != null" class="cbi-conf-badge"
                                                  :class="it.confidence>=0.7?'high':it.confidence>=0.4?'mid':'low'">
                                                {{ Math.round(it.confidence*100) }}%
                                            </span>
                                            <span v-else style="font-size:0.75rem;color:var(--gray-400);">—</span>
                                        </td>
                                        <td class="cbi-td">
                                            <span :class="it.is_shared ? 'cbi-shared-badge' : 'cbi-private-badge'">
                                                {{ it.is_shared ? '🌐 網域共用' : '🔒 個人' }}
                                            </span>
                                        </td>
                                        <td v-if="isDomainAdmin || isSuperAdmin" class="cbi-td">
                                            <div v-if="cbiShareDraft[it.id]" class="flex flex-wrap items-center gap-1">
                                                <select v-model="cbiShareDraft[it.id].scope" class="input" style="font-size:0.8rem;padding:0.2rem 0.4rem;">
                                                    <option value="domain">網域共用</option>
                                                    <option value="user">指派使用者</option>
                                                </select>
                                                <select v-if="(cbiShareDraft[it.id].scope || 'user') === 'user'"
                                                    v-model.number="cbiShareDraft[it.id].user_id"
                                                    class="input" style="font-size:0.8rem;padding:0.2rem 0.4rem;">
                                                    <option v-for="u in cbiAssignableUsers" :key="u.id" :value="u.id">
                                                        {{ u.email || ('#' + u.id) }}
                                                    </option>
                                                </select>
                                                <button @click="applyCbiShare(it)" :disabled="cbiShareSavingId === it.id"
                                                    class="btn btn-primary btn-sm">
                                                    {{ cbiShareSavingId === it.id ? '套用中...' : '套用' }}
                                                </button>
                                            </div>
                                            <div v-else class="text-xs" style="color:var(--gray-400);">—</div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Pagination -->
                        <div class="mt-4 flex items-center justify-between gap-3">
                            <div class="text-sm" style="color:var(--gray-500);">共 <strong>{{ cbiTotal || 0 }}</strong> 筆</div>
                            <div class="flex items-center gap-2">
                                <button @click="cbiPrevPage" :disabled="cbiOffset <= 0"
                                    class="btn btn-secondary btn-sm">← 上一頁</button>
                                <span class="text-sm" style="color:var(--gray-500);">
                                    第 {{ Math.floor(cbiOffset / cbiLimit) + 1 }} 頁
                                </span>
                                <button @click="cbiNextPage" :disabled="(cbiOffset + cbiLimit) >= (cbiTotal || 0)"
                                    class="btn btn-secondary btn-sm">下一頁 →</button>
                            </div>
                        </div>
                    </div>

                    <!-- AI Playground Tab -->
                    <!-- AI Settings Tab -->
                    <div v-if="aiTab === 'settings'" class="card p-6 max-w-2xl">
                        <h3 class="font-bold text-lg mb-6 pb-2" style="color:var(--gray-700);border-bottom:1px solid var(--border);">AI 服務參數設定</h3>

                        <div class="mb-6">
                            <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">AI 提供者 (AI Provider)</label>
                            <div class="flex gap-2 mb-4">
                                <select v-model="aiSettingsForm.ai_provider"
                                    @change="saveAiSetting('ai_provider', aiSettingsForm.ai_provider)"
                                    class="input w-full">
                                    <option value="gateway">中央 LLM Gateway (統一 / 推薦)</option>
                                    <option value="google">Google Gemini (Cloud)</option>
                                    <option value="ollama">Ollama (Local)</option>
                                    <option value="lmstudio">LM Studio (Local)</option>
                                </select>
                            </div>

                            <!-- LLM Gateway Settings -->
                            <div v-if="aiSettingsForm.ai_provider === 'gateway'"
                                class="card p-4" style="border-left:3px solid var(--brand-600);">
                                <div class="mb-4">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">Gateway Base URL</label>
                                    <div class="flex gap-2">
                                        <input v-model="aiSettingsForm.gateway_base_url" type="text"
                                            class="input flex-1"
                                            placeholder="http://host.docker.internal:4000">
                                        <button @click="saveAiSetting('gateway_base_url', aiSettingsForm.gateway_base_url)"
                                            class="btn btn-primary">儲存</button>
                                    </div>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">統一推理閘道 (LiteLLM, OpenAI 相容)。雲端金鑰由閘道集中保管,本系統不持有。</p>
                                </div>
                                <div class="mb-4">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">API Key (per-tenant)</label>
                                    <div class="flex gap-2">
                                        <input v-model="aiSettingsForm.gateway_api_key" type="password"
                                            class="input flex-1" placeholder="sk-..." autocomplete="off">
                                        <button @click="saveAiSetting('gateway_api_key', aiSettingsForm.gateway_api_key)"
                                            class="btn btn-primary">儲存</button>
                                    </div>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">由平台為本租戶核發的虛擬金鑰,可單獨撤銷。</p>
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">預設模型</label>
                                    <div class="flex gap-2">
                                        <select v-if="gatewayModels.length"
                                            v-model="aiSettingsForm.gateway_model"
                                            class="input flex-1">
                                            <option v-for="m in gatewayModels" :key="m" :value="m">{{ m }}</option>
                                        </select>
                                        <input v-else v-model="aiSettingsForm.gateway_model" type="text"
                                            class="input flex-1" placeholder="gpt-4o（點 🔄 載入）">
                                        <button @click="fetchAiModels('gateway')"
                                            class="btn btn-secondary btn-sm"
                                            title="從閘道載入可用模型">🔄</button>
                                        <button @click="saveAiSetting('gateway_model', aiSettingsForm.gateway_model)"
                                            class="btn btn-primary">儲存</button>
                                    </div>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">切換到此提供者時會自動載入模型清單，亦可按 🔄 重新整理。</p>
                                </div>
                            </div>

                            <!-- Google Settings -->
                            <div v-if="aiSettingsForm.ai_provider === 'google'"
                                class="card p-4" style="border-left:3px solid var(--brand-400);">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">Google Gemini API Key</label>
                                <div class="flex gap-2">
                                    <input v-model="aiSettingsForm.google_api_key" type="password"
                                        class="flex-1 border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="AIzaSy..." autocomplete="off">
                                    <button @click="saveAiSetting('google_api_key', aiSettingsForm.google_api_key)"
                                        class="btn btn-primary">儲存</button>
                                </div>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">請前往 Google AI Studio 申請 API Key 以啟用進階 AI 功能。</p>
                            </div>

                            <!-- Ollama Settings -->
                            <div v-if="aiSettingsForm.ai_provider === 'ollama'"
                                class="card p-4" style="border-left:3px solid var(--color-success);">
                                <div class="mb-4">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">Ollama Base URL</label>
                                    <div class="flex gap-2">
                                        <input v-model="aiSettingsForm.ollama_base_url" type="text"
                                            class="input flex-1"
                                            placeholder="http://host.docker.internal:11434">
                                        <button
                                            @click="saveAiSetting('ollama_base_url', aiSettingsForm.ollama_base_url)"
                                            class="btn btn-primary">儲存</button>
                                    </div>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">Docker 內部連線請使用 host.docker.internal</p>
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">Model Name</label>
                                    <div class="flex gap-2">
                                        <select v-if="ollamaModels.length"
                                            v-model="aiSettingsForm.ollama_model"
                                            class="input flex-1">
                                            <option v-for="m in ollamaModels" :key="m" :value="m">{{ m }}</option>
                                        </select>
                                        <input v-else v-model="aiSettingsForm.ollama_model" type="text"
                                            class="input flex-1" placeholder="llama3（點 🔄 自動載入）">
                                        <button @click="fetchAiModels('ollama')"
                                            class="btn btn-secondary btn-sm"
                                            title="重新載入模型清單">🔄</button>
                                        <button @click="saveAiSetting('ollama_model', aiSettingsForm.ollama_model)"
                                            class="btn btn-primary">儲存</button>
                                    </div>
                                </div>
                            </div>

                            <!-- LM Studio Settings -->
                            <div v-if="aiSettingsForm.ai_provider === 'lmstudio'"
                                class="card p-4" style="border-left:3px solid var(--brand-600);">
                                <div class="mb-4">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">LM Studio Base URL</label>
                                    <div class="flex gap-2">
                                        <input v-model="aiSettingsForm.lmstudio_base_url" type="text"
                                            class="input flex-1"
                                            placeholder="http://host.docker.internal:1234">
                                        <button
                                            @click="saveAiSetting('lmstudio_base_url', aiSettingsForm.lmstudio_base_url)"
                                            class="btn btn-primary">儲存</button>
                                    </div>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">Docker 內部連線請使用 host.docker.internal，並確認 LM
                                        Studio 已開啟 Server 模式 (Port 1234)</p>
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">Model Name</label>
                                    <div class="flex gap-2">
                                        <select v-if="lmstudioModels.length"
                                            v-model="aiSettingsForm.lmstudio_model"
                                            class="input flex-1">
                                            <option v-for="m in lmstudioModels" :key="m" :value="m">{{ m }}</option>
                                        </select>
                                        <input v-else v-model="aiSettingsForm.lmstudio_model" type="text"
                                            class="input flex-1" placeholder="local-model（點 🔄 自動載入）">
                                        <button @click="fetchAiModels('lmstudio')"
                                            class="btn btn-secondary btn-sm"
                                            title="重新載入模型清單">🔄</button>
                                        <button @click="saveAiSetting('lmstudio_model', aiSettingsForm.lmstudio_model)"
                                            class="btn btn-primary">儲存</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card p-4 mt-6" style="border-left:3px solid var(--color-warning);">
                            <div class="font-bold mb-3" style="color:var(--gray-700);">圖片/附件分析 (VLM)</div>
                            <div class="mb-4">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">VLM 提供者</label>
                                <div class="flex gap-2">
                                    <select v-model="aiSettingsForm.vision_provider" class="input flex-1"
                                        @change="saveAiSetting('vision_provider', aiSettingsForm.vision_provider)">
                                        <option value="gateway">中央 LLM Gateway (統一)</option>
                                        <option value="lmstudio">LM Studio (Local)</option>
                                        <option value="ollama">Ollama (Local)</option>
                                        <option value="google">Google (Cloud)</option>
                                    </select>
                                    <button @click="saveAiSetting('vision_provider', aiSettingsForm.vision_provider)"
                                        class="btn btn-warning">儲存</button>
                                </div>
                            </div>

                            <div class="mb-4">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">VLM Base URL</label>
                                <div class="flex gap-2">
                                    <input v-model="aiSettingsForm.vision_base_url" type="text"
                                        class="input flex-1"
                                        placeholder="http://host.docker.internal:11434 或 http://host.docker.internal:1234">
                                    <button @click="saveAiSetting('vision_base_url', aiSettingsForm.vision_base_url)"
                                        class="btn btn-warning">儲存</button>
                                </div>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">留空則自動沿用該提供者的預設 Base URL。</p>
                            </div>

                            <div>
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">VLM Model Name</label>
                                <div class="flex gap-2">
                                    <select v-if="visionModels.length"
                                        v-model="aiSettingsForm.vision_model"
                                        class="input flex-1">
                                        <option v-for="m in visionModels" :key="m" :value="m">{{ m }}</option>
                                    </select>
                                    <input v-else v-model="aiSettingsForm.vision_model" type="text"
                                        class="input flex-1"
                                        placeholder="例如 qwen2.5-vl / llava / gemma-3-...（點 🔄 自動載入）">
                                    <button
                                        @click="fetchAiModels(aiSettingsForm.vision_provider, aiSettingsForm.vision_base_url)"
                                        class="btn btn-secondary btn-sm"
                                        title="重新載入模型清單">🔄</button>
                                    <button @click="saveAiSetting('vision_model', aiSettingsForm.vision_model)"
                                        class="btn btn-warning">儲存</button>
                                </div>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">切換 VLM 提供者時會自動載入模型清單。當 Playground 有上傳圖片時，生成摘要 / 建議回覆 / 情緒分析會使用這裡的模型。
                                </p>
                            </div>
                        </div>

                        <!-- Image Generation Settings -->
                        <div class="card p-4 mt-6" style="border-left:3px solid #6366f1;">
                            <div class="font-bold mb-3" style="color:var(--gray-700);">🎨 圖像生成 (AI Visual Creation)</div>

                            <div class="mb-4">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">圖像生成服務</label>
                                <div class="flex gap-2">
                                    <select v-model="aiSettingsForm.image_provider" class="input flex-1"
                                        @change="saveAiSetting('image_provider', aiSettingsForm.image_provider)">
                                        <option value="gateway">中央 LLM Gateway (統一 / 推薦)</option>
                                        <option value="google_imagen">Google Imagen 3</option>
                                        <option value="openai">DALL-E 3 (OpenAI)</option>
                                        <option value="flux">FLUX via fal.ai</option>
                                        <option value="stability">Stability AI</option>
                                    </select>
                                    <button @click="saveAiSetting('image_provider', aiSettingsForm.image_provider)"
                                        class="btn btn-primary">儲存</button>
                                </div>
                            </div>

                            <div class="mb-2">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">
                                    API Key
                                    <span v-if="aiSettingsForm.image_provider === 'google_imagen'"
                                        class="text-xs font-normal ml-1" style="color:var(--gray-500);">
                                        — 與上方 Google Gemini API Key 相同，可直接填入
                                    </span>
                                </label>
                                <div class="flex gap-2">
                                    <input v-model="aiSettingsForm.image_api_key" type="password"
                                        class="input flex-1"
                                        :placeholder="aiSettingsForm.image_provider === 'google_imagen' ? 'AIzaSy... (Google AI Studio API Key)' :
                                                      aiSettingsForm.image_provider === 'openai' ? 'sk-... (OpenAI API Key)' :
                                                      aiSettingsForm.image_provider === 'flux' ? 'fal.ai API Key' :
                                                      'Stability AI API Key'"
                                        autocomplete="off">
                                    <button @click="saveAiSetting('image_api_key', aiSettingsForm.image_api_key)"
                                        class="btn btn-primary">儲存</button>
                                </div>
                                <p class="text-xs mt-2" style="color:var(--gray-500);">
                                    <span v-if="aiSettingsForm.image_provider === 'google_imagen'">
                                        前往 <a href="https://aistudio.google.com/apikey" target="_blank" style="color:var(--brand-500);">Google AI Studio</a> 取得 API Key。每日有免費配額。
                                    </span>
                                    <span v-else-if="aiSettingsForm.image_provider === 'openai'">
                                        前往 <a href="https://platform.openai.com/api-keys" target="_blank" style="color:var(--brand-500);">OpenAI Platform</a> 取得 API Key。約 $0.04–$0.12 / 張。
                                    </span>
                                    <span v-else-if="aiSettingsForm.image_provider === 'flux'">
                                        前往 <a href="https://fal.ai" target="_blank" style="color:var(--brand-500);">fal.ai</a> 取得 API Key。FLUX 模型品質極佳，約 $0.005–$0.05 / 張。
                                    </span>
                                    <span v-else-if="aiSettingsForm.image_provider === 'stability'">
                                        前往 <a href="https://platform.stability.ai" target="_blank" style="color:var(--brand-500);">Stability AI Platform</a> 取得 API Key。約 $0.002–$0.065 / 張。
                                    </span>
                                </p>
                            </div>
                        </div>

                        <div class="card p-4 mt-6" style="border-left:3px solid var(--color-success);">
                            <div class="font-bold mb-3" style="color:var(--gray-700);">CBI 官網最小抓取</div>

                            <div class="mb-4">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">啟用官網抓取</label>
                                <div class="flex gap-2 items-center h-10">
                                    <label class="inline-flex items-center gap-3 cursor-pointer">
                                        <button type="button"
                                            @click="aiSettingsForm.cbi_crawl_enabled = !aiSettingsForm.cbi_crawl_enabled; saveAiSetting('cbi_crawl_enabled', aiSettingsForm.cbi_crawl_enabled)"
                                            class="relative inline-flex items-center rounded-full transition-colors flex-shrink-0"
                                            :style="{ width:'44px', height:'24px', background: aiSettingsForm.cbi_crawl_enabled ? 'var(--color-success)' : 'var(--gray-300)' }">
                                            <span class="inline-block rounded-full transition-transform"
                                                :style="{ width:'18px', height:'18px', background:'#fff', transform: aiSettingsForm.cbi_crawl_enabled ? 'translateX(23px)' : 'translateX(3px)', boxShadow:'var(--shadow-sm)' }"></span>
                                        </button>
                                        <span class="text-sm font-medium" style="color:var(--gray-900);">{{
                                            aiSettingsForm.cbi_crawl_enabled ? '已啟用' : '已停用' }}</span>
                                    </label>
                                </div>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">啟用後，CBI 會嘗試抓取寄件者網域與內文 URL 的少量頁面，產生 evidence 片段。</p>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">每次分析最多抓取頁數</label>
                                    <div class="flex gap-2">
                                        <input v-model.number="aiSettingsForm.cbi_crawl_max_pages" type="number" min="0" step="1"
                                            class="input flex-1" />
                                        <button @click="saveAiSetting('cbi_crawl_max_pages', aiSettingsForm.cbi_crawl_max_pages)"
                                            class="btn btn-success">儲存</button>
                                    </div>
                                </div>

                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">單頁超時秒數</label>
                                    <div class="flex gap-2">
                                        <input v-model.number="aiSettingsForm.cbi_crawl_timeout_seconds" type="number" min="1" step="1"
                                            class="input flex-1" />
                                        <button @click="saveAiSetting('cbi_crawl_timeout_seconds', aiSettingsForm.cbi_crawl_timeout_seconds)"
                                            class="btn btn-success">儲存</button>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">根網域允許抓取的路徑（逗號分隔）</label>
                                <div class="flex gap-2">
                                    <input v-model="aiSettingsForm.cbi_crawl_allowed_paths" type="text"
                                        class="input flex-1"
                                        placeholder="/,/about,/products,/solutions,/contact" />
                                    <button @click="saveAiSetting('cbi_crawl_allowed_paths', aiSettingsForm.cbi_crawl_allowed_paths)"
                                        class="btn btn-success">儲存</button>
                                </div>
                            </div>

                            <div class="mt-4">
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">User-Agent</label>
                                <div class="flex gap-2">
                                    <input v-model="aiSettingsForm.cbi_crawl_user_agent" type="text"
                                        class="input flex-1" placeholder="ai-mail-cbi/1.0" />
                                    <button @click="saveAiSetting('cbi_crawl_user_agent', aiSettingsForm.cbi_crawl_user_agent)"
                                        class="btn btn-success">儲存</button>
                                </div>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">安全限制：僅抓取 http/https，拒絕 localhost 與內網 IP。</p>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">垃圾郵件判斷門檻值</label>
                                <div class="flex gap-2">
                                    <input v-model="aiSettingsForm.spam_threshold" type="number" step="0.1"
                                        class="input flex-1">
                                    <button @click="saveAiSetting('spam_threshold', aiSettingsForm.spam_threshold)"
                                        class="btn btn-primary btn-sm text-sm">更新</button>
                                </div>
                            </div>

                            <div>
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">啟用 AI 輔助分析</label>
                                <div class="flex gap-2 items-center h-10">
                                    <label class="inline-flex items-center gap-3 cursor-pointer">
                                        <button type="button"
                                            @click="aiSettingsForm.use_ai_analysis = !aiSettingsForm.use_ai_analysis; saveAiSetting('use_ai_analysis', aiSettingsForm.use_ai_analysis)"
                                            class="relative inline-flex items-center rounded-full transition-colors flex-shrink-0"
                                            :style="{ width:'44px', height:'24px', background: aiSettingsForm.use_ai_analysis ? 'var(--brand-500)' : 'var(--gray-300)' }">
                                            <span class="inline-block rounded-full transition-transform"
                                                :style="{ width:'18px', height:'18px', background:'#fff', transform: aiSettingsForm.use_ai_analysis ? 'translateX(23px)' : 'translateX(3px)', boxShadow:'var(--shadow-sm)' }"></span>
                                        </button>
                                        <span class="text-sm font-medium" style="color:var(--gray-900);">{{
                                            aiSettingsForm.use_ai_analysis ? '已啟用' : '已停用' }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


</template>

<script setup>
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  aiTab, isSuperAdmin,
  ragStats, isSyncing, syncMessage, ragQueryInput, ragLoading, ragResult,
  kbAdminQueryInput, kbAdminLoading, kbAdminResult, kbAdminSources, kbAdminError,
  kbDocs, kbDocsLoading, kbNewDocTitle, kbNewDocContent, kbSaving,
  cbiFilters, cbiOffset, cbiItems, cbiTotal, cbiLimit, cbiShareDraft, cbiShareSavingId,
  aiSettingsForm, editingAiSetting, aiSettings,
  aiTest, aiLoading, aiResult,
  ollamaModels, lmstudioModels, gatewayModels,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  syncRagMaildir, performRagQuery, performKnowledgeQuery,
  fetchKnowledgeDocuments, addKnowledgeDocument,
  resetCbiFilters, cbiPrevPage, cbiNextPage, applyCbiShare,
  fetchAiSettings, isSensitiveSettingKey, maskSettingValue, saveAiSetting,
  testAiSpam, generateSummary, analyzeSentiment, generateReply,
  handleAiImageUpload, removeAiImage,
  fetchAiModels,
} = store

// VLM 下拉模型清單（依目前選擇的 vision_provider 決定）
const visionModels = computed(() => {
  const p = aiSettingsForm.value?.vision_provider
  if (p === 'ollama') return ollamaModels.value
  if (p === 'lmstudio') return lmstudioModels.value
  if (p === 'gateway') return gatewayModels.value
  return []
})

function _fetchModelsForCurrentProviders() {
  const p = aiSettingsForm.value?.ai_provider
  if (p === 'ollama') fetchAiModels('ollama')
  else if (p === 'lmstudio') fetchAiModels('lmstudio')
  else if (p === 'gateway') fetchAiModels('gateway')

  const vp = aiSettingsForm.value?.vision_provider
  if ((vp === 'ollama' || vp === 'lmstudio' || vp === 'gateway') && vp !== p) {
    fetchAiModels(vp, aiSettingsForm.value?.vision_base_url)
  }
}

// 切換 AI Provider 時自動拉取可用模型
watch(() => aiSettingsForm.value?.ai_provider, (p) => {
  if (p === 'ollama') fetchAiModels('ollama')
  else if (p === 'lmstudio') fetchAiModels('lmstudio')
  else if (p === 'gateway') fetchAiModels('gateway')
})

// 切換 VLM Provider 時自動拉取可用模型
watch(() => aiSettingsForm.value?.vision_provider, (p) => {
  if (p === 'ollama' || p === 'lmstudio' || p === 'gateway') {
    fetchAiModels(p, aiSettingsForm.value?.vision_base_url)
  }
})

// 進入 settings tab 時主動拉一次（解決預設值與儲存值相同、watch 不觸發的問題）
watch(aiTab, (tab) => {
  if (tab === 'settings') _fetchModelsForCurrentProviders()
})
</script>

<style scoped>
/* CBI Section Header */
.cbi-section-header {
  display:flex; align-items:center; justify-content:space-between; gap:1rem;
  padding:1rem 1.25rem; border-radius:var(--radius-lg,0.5rem);
  background:linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%);
}

/* Filter Labels */
.cbi-filter-label {
  display:block; font-size:0.75rem; font-weight:700;
  color:var(--gray-600); margin-bottom:0.25rem;
}

/* Table */
.cbi-page-table { border-collapse:collapse; width:100%; font-size:0.875rem; }
.cbi-th {
  padding:0.5rem 0.75rem; text-align:left;
  font-size:0.75rem; font-weight:700; color:var(--gray-600); white-space:nowrap;
}
.cbi-td { padding:0.625rem 0.75rem; vertical-align:middle; }
.cbi-page-row { border-bottom:1px solid var(--gray-100); transition:background 0.12s; }
.cbi-page-row:hover { background:var(--gray-50); }

/* Confidence Badge */
.cbi-conf-badge {
  display:inline-block;
  padding:0.125rem 0.5rem; border-radius:9999px; font-size:0.7rem; font-weight:700;
}
.cbi-conf-badge.high { background:#dcfce7; color:#15803d; }
.cbi-conf-badge.mid  { background:#fef3c7; color:#b45309; }
.cbi-conf-badge.low  { background:#fee2e2; color:#b91c1c; }

/* Share Status Badge */
.cbi-shared-badge {
  display:inline-block;
  padding:0.125rem 0.5rem; border-radius:9999px; font-size:0.7rem; font-weight:600;
  background:#e0e7ff; color:#4f46e5;
}
.cbi-private-badge {
  display:inline-block;
  padding:0.125rem 0.5rem; border-radius:9999px; font-size:0.7rem; font-weight:600;
  background:#f3f4f6; color:var(--gray-500);
}

@keyframes cbi-spin {
  from { transform:rotate(0deg); }
  to   { transform:rotate(360deg); }
}
</style>

